import { useDebouncedCallback } from '@mantine/hooks';
import { SortingState } from '@tanstack/react-table';
import { noop } from 'lodash-es';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useComputedStateFirstNotNull } from 'src/hooks/useComputedState';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  ActionOutboxEntityType,
  Feature,
  UserSetting,
} from 'src/WebApiController';

export enum TableViewMode {
  InsightView,
  SearchView,
  EventView,
}

export type IActiveSortTableColumnContext = {
  sorting: SortingState;
  setSorting: (val: SortingState) => void;
};

export const CreateActiveSortTableColumnContext =
  createContext<IActiveSortTableColumnContext>({
    sorting: [],
    setSorting: noop,
  });

export const useActiveSortTableColumnContext = () =>
  useContext(CreateActiveSortTableColumnContext);

export const ActiveSortTableColumnProvider = ({
  entityType,
  viewMode = TableViewMode.EventView,
  defaultSorting,
  children,
}: {
  children: React.ReactNode;
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase;
  viewMode?: TableViewMode;
  defaultSorting: SortingState;
}) => {
  const hasUserSettingOnTableSortingFeature = useUserHasFeature(
    Feature.UserSettingOnTableSorting
  );

  const userSettingSortingId = useMemo(() => {
    switch (entityType) {
      case ActionOutboxEntityType.Listing:
        return viewMode === TableViewMode.InsightView
          ? UserSetting.InventoryPortfolioColumnSort
          : viewMode === TableViewMode.SearchView
            ? UserSetting.InventorySearchColumnSort
            : UserSetting.InventoryEventColumnSort;
      case ActionOutboxEntityType.Purchase:
        return viewMode === TableViewMode.InsightView
          ? UserSetting.PurchasePortfolioColumnSort
          : viewMode === TableViewMode.SearchView
            ? UserSetting.PurchaseSearchColumnSort
            : UserSetting.PurchaseEventColumnSort;
      case ActionOutboxEntityType.Sale:
        return viewMode === TableViewMode.InsightView
          ? UserSetting.SalePortfolioColumnSort
          : viewMode === TableViewMode.SearchView
            ? UserSetting.SaleSearchColumnSort
            : UserSetting.SaleEventColumnSort;
      default:
        return undefined;
    }
  }, [entityType, viewMode]);

  const { value, setUserSetting } = useServerUserSetting<SortingState>({
    id: userSettingSortingId,
  });
  const featureGatedValue = useMemo((): SortingState | undefined => {
    if (!hasUserSettingOnTableSortingFeature) {
      return defaultSorting;
    }
    return value;
  }, [defaultSorting, hasUserSettingOnTableSortingFeature, value]);

  // An update will update the local state, and kick off a server update.
  // Don't want to update the sorting for every server value update,
  // which can cause the sorting state to be funky between user update and server update.
  const [sorting, setSorting] = useComputedStateFirstNotNull(
    featureGatedValue,
    defaultSorting,
    true
  );

  // Important, this is to make sure sorting is reset when switching pages
  const viewModeStack = useRef<TableViewMode>();
  useEffect(() => {
    if (
      viewMode !== viewModeStack.current &&
      featureGatedValue &&
      featureGatedValue !== sorting
    ) {
      setSorting(featureGatedValue);
      viewModeStack.current = viewMode;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, featureGatedValue]);

  const debouncedSetUserSorting = useDebouncedCallback(setUserSetting, 300);
  const setSortingInternal = useCallback(
    (updated: SortingState) => {
      setSorting(updated);
      if (hasUserSettingOnTableSortingFeature) {
        debouncedSetUserSorting(updated);
      }
    },
    [debouncedSetUserSorting, hasUserSettingOnTableSortingFeature, setSorting]
  );

  return (
    <CreateActiveSortTableColumnContext.Provider
      value={{
        sorting,
        setSorting: setSortingInternal,
      }}
    >
      {children}
    </CreateActiveSortTableColumnContext.Provider>
  );
};
